/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type ButtonProps,
  type ChipProps,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { BASE_SF_URL, type SalesforceObject } from '@poweralpha/common';
import { useCallback } from 'react';
import SfIcon from '../../icons/salesforce.svg?react';
import DisabledSFIcon from '../../icons/salesforce-grey.svg?react';
import { StdButton } from '../buttons';

export const SalesforceIcon = ({
  disabled,
  height = 30,
}: {
  disabled?: boolean;
  height?: number;
}) => (disabled ? <DisabledSFIcon height={height} /> : <SfIcon height={height} />);

interface Props {
  id: string | null | undefined;
  object: SalesforceObject;
  label?: string;
  color?: ChipProps['color'] & ButtonProps['color'];
  button?: boolean;
  alignRight?: boolean;
  height?: number;
  isLoading?: boolean;
  omitIcon?: boolean;
}

function SalesforceLinkIcon({
  id,
  object,
  label,
  color,
  button,
  alignRight,
  height = 30,
  isLoading,
  omitIcon,
}: Readonly<Props>) {
  const disabled = !id;

  const handleClick = useCallback(
    () => window.open(`${BASE_SF_URL}/r/${object}/${id}/view`),
    [id, object],
  );

  if (isLoading) {
    return <Skeleton width="100%" />;
  }

  if (button) {
    return (
      <StdButton
        label={label ?? 'bnCRM'}
        color={color}
        variant="contained"
        onClick={() => window.open(`${BASE_SF_URL}/r/${object}/${id}/view`)}
      />
    );
  }

  if (!label) {
    return (
      <IconButton size="small" color={color} onClick={handleClick} disabled={disabled}>
        <SalesforceIcon height={height} disabled={disabled} />
      </IconButton>
    );
  }

  return (
    <MuiLink href={`${BASE_SF_URL}/r/${object}/${id}/view`} target="_blank">
      <Stack direction={alignRight ? 'row-reverse' : 'row'} spacing={1}>
        {!omitIcon && <SfIcon height={20} />}
        <Typography
          noWrap
          sx={{
            fontSize: 14,
            alignItems: 'center',
          }}
        >
          {label}
        </Typography>
      </Stack>
    </MuiLink>
  );
}

export default SalesforceLinkIcon;
